import React, { ReactNode, createContext, useContext, useEffect, useMemo, useState } from 'react';
import Routes from '@services/Routes';
import { formValueSelector, getFormValues, change, reset } from 'redux-form';
import { connect, useStore } from 'react-redux';
import { ConfigType, LogoPositionType, TempType, UserType, setUser } from '@store/actions';
import { checkUser } from '@utils/reduxUtils';
import { RootState } from '@store/reducers';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useDispatch } from 'react-redux';
import TagManager from 'react-gtm-module';
import { flowStepForPath, flowStepCompleteForPath } from '@utils/gaFlow';
import {
  ApplyWizardStep,
  ApplyWizardStepObject,
  STEP_AMOUNT,
  STEP_BUSINESS_FURTHER_DETAILS,
  STEP_BUSINESS_MORE_DETAILS,
  STEP_BUSINESS_OWNERS,
  STEP_HOMEPAGE,
  STEP_BCM_HOMEPAGE,
  STEP_INFO,
  STEP_SUMMARY,
  STEP_TYPE_OF_FUNDING,
  STEP_VERIFY_LOGIN,
  STEP_WHEN_FUNDING_NEEDED,
  STEP_BCM_INFO,
  STEP_BCM_BUSINESS_MORE_DETAILS,
  STEP_BCM_SUMMARY,
  STEP_INTEGRATED_LOGIN,
  STEP_BCM_2_HOMEPAGE,
  STEP_BCM_2_INFO,
  STEP_EQ_LEAD_HOMEPAGE,
  APPLY_LOAN_STEPS,
  APPLY_BCM_STEPS_SHORT,
  APPLY_BCM_STEPS,
} from '@components/ApplyWizard/ApplyWizardSteps';
import { setFarthestStep as setFarthestStepAction } from '../redux/actions';
import AmountContainer from '@components/Apply/Amount';
import Auth0CallbackContainer from '@components/Apply/Auth0';
import { MoreDetailsOnYourBusiness } from '@components/Apply/MoreDetailsOnYourBusiness/MoreDetailsOnYourBusiness';
import { FurtherBusinessDetails } from '@components/Apply/FurtherBusinessDetails/FurtherBusinessDetails';
import { OwnersMoreDetailsAboutYou } from '@components/Apply/OwnersMoreDetailsAboutYou/OwnersMoreDetailsAboutYou';
import { FundingIsAlmostHere } from '@components/Apply/FundingIsAlmostHere/FundingIsAlmostHere';
import { TypeOfFunding } from '@components/Apply/TypeOfFunding/TypeOfFunding';
import { WhenFundingNeeded } from '@components/Apply/WhenFundingNeeded/WhenFundingNeeded';
import { BCMMoreDetailsOnYourBusiness } from '@components/Apply/MoreDetailsOnYourBusiness/BCMMoreDetailsOnYourBusiness';
import { BCMSummary } from '@components/Apply/BCMSummary/BCMSummary';
import BCMHome from '@components/BCM/BCMHome';
import { ApplyWizardStepsWithComponents } from '@components/ApplyWizard/ApplyWizardStepsWithComponents';

export type ApplyWizardContextType = Partial<{
  applicationFlow: 'apply' | 'bcm' | 'bcm-short' | 'eqlead';
  nextRoute: () => void;
  previousRoute: () => void;
  dataLayerPush: (event: any, post: any) => void;
  saving: boolean;
  setSaving: (saving: boolean) => void;
  steps: ApplyWizardStepObject[];
  currentStep: ApplyWizardStepObject;
  currentStepIndex: number;
  changeStepTo: (step: ApplyWizardStep) => void;
  resetFarthestStep: () => void;
  changeStepToFirstNotValid: () => void;
  changeStepToFirst: () => void;
  farthestStep: ApplyWizardStepObject;
  isInWizard: boolean;
  showStepNavigation: boolean;
  currentPath: string;
  setCurrentPath: (path: string) => void;
  changeStepAfterLogin: () => void;
  setStepDirection: (direction: 'next' | 'prev') => void;
  stepDirection: 'next' | 'prev';
  /** Redux `state.temp` (wizard scratch state — pendingOffers, declineDecision, …). Mapped into the provider since the E5 decline reveal needed it; was previously provider-internal. */
  temp: TempType;
}>;

export const ApplyWizardContext = createContext<ApplyWizardContextType>({});

const a = [
  '/',
  'amount',
  'info',
  'business/more-details',
  'business/further-details',
  'type-of-funding',
  'business/owners',
  'when-funding-needed',
  'funding-is-almost-here',
];

export const ApplyWizardProviderConnected = ({
  bcmWizardRoutes = [
    '/bcm2',
    '/bcm2/app/info',
    '/bcm2/app/business/more-details',
    '/bcm2/app/summary',
    '/bcm2/app/contact-soon',
  ],
  bcmShortWizardRoutes = [
    '/bcm',
    '/bcm/app/info',
    '/bcm/app/business/more-details',
    // Optional add-a-card step (billed cohort) — always in the sequence;
    // the step itself forwards to contact-soon unless the server-side
    // billing-config.applyCardStepEnabled flag is on.
    '/bcm/app/add-card',
    '/bcm/app/contact-soon',
  ],
  applicationType,
  children,
  routes,
  vendor_name,
  business_id,
  loan_id,
  contact_id,
  loan_type,
  loan_terms,
  allValues,
  theme,
  config,
  temp,
  user,
  images,
  partnerCustomFields,
  match,
  location,
  history,
  finalWizardRoutes,
  farthestStep,
  disableAuth0ExternalUniversalLogin = false,
}: RouteComponentProps<{
  bcmWizardRoutes?: ApplyWizardStep[];
  farthestStep: ApplyWizardStepObject;
  children?: ReactNode;
  routes: string[];
  finalWizardRoutes: ApplyWizardStep[];
  vendor_name: string;
  business_id: string;
  loan_id: string;
  contact_id: string;
  loan_type: string;
  loan_terms: number;
  allValues: any;
  theme: {
    primary: string;
    primaryRgba: string;
    secondary: string;
    logoScale: number;
    name: string;
    palette?: {
      error?: { dark: string };
      primary?: { dark: string };
    };
    logoPosition: LogoPositionType;
  };
  config: ConfigType;
  temp: TempType;
  user: UserType['user'];
  images: any;
  partnerCustomFields: { [a: string]: string };
  withCustomLogin?: boolean;
}>) => {
  const store = useStore();
  const [currentPath, setCurrentPath] = useState(location.pathname);
  // There is a bug that when some loan page is opened directly and BCM was set before then it might pickup BCM flow instead of loan. Monkeypatch here ensures that if it is opened at "/" in iframe/popup it does not happen.
  // Loan Homepage will reset applicationType to "apply" in Redux.
  const fallbackApplicationTypeDetermined =
    currentPath === '/'
      ? 'apply'
      : applicationType === 'bcm-short' || currentPath.startsWith('/bcm')
      ? 'bcm-short'
      : applicationType === 'bcm' || currentPath.startsWith('/bcm2')
      ? 'bcm'
      : 'apply'; // Apply is a standard flow, sometimes BCM might be set for /bcm2 route.
  const applicationTypeDetermined = currentPath.startsWith('/eq')
    ? 'eqlead'
    : APPLY_LOAN_STEPS.includes(currentPath) || APPLY_LOAN_STEPS.some((r) => `/app${r}` === currentPath)
    ? 'apply'
    : APPLY_BCM_STEPS_SHORT.includes(currentPath)
    ? 'bcm-short'
    : APPLY_BCM_STEPS.includes(currentPath)
    ? 'bcm'
    : fallbackApplicationTypeDetermined;
  console.log('applicationTypeDetermined', applicationTypeDetermined, currentPath);
  const [steps, setSteps] = useState<ApplyWizardStepObject[]>([
    ...finalWizardRoutes
      // STEP_WHEN_FUNDING_NEEDED removed from the loan flow per Apply QA.
      // The route originates from the brand config (state.brand.data.routes)
      // so it must be filtered here on the client until the brand config is
      // updated server-side. Once that's done, this filter can be dropped.
      .filter((r: ApplyWizardStep) => r !== STEP_WHEN_FUNDING_NEEDED)
      .filter((r: ApplyWizardStep) => !!ApplyWizardStepsWithComponents[r])
      .map((r: ApplyWizardStep) => ApplyWizardStepsWithComponents[r]),
    // Standard Steps:
    ...(finalWizardRoutes.includes(STEP_VERIFY_LOGIN) ? [] : [ApplyWizardStepsWithComponents[STEP_VERIFY_LOGIN]]),
    ...(disableAuth0ExternalUniversalLogin ? [] : [ApplyWizardStepsWithComponents[STEP_INTEGRATED_LOGIN]]),
  ]);
  console.log('steps', steps, finalWizardRoutes);

  const foundStepBasedOnPath = (ApplyWizardStepsWithComponents as any)[
    Object.keys(ApplyWizardStepsWithComponents).find(
      (key) =>
        (ApplyWizardStepsWithComponents as any)[key] &&
        (ApplyWizardStepsWithComponents as any)[key].path === currentPath
    ) ?? ''
  ];
  console.log('foundStepBasedOnPath', foundStepBasedOnPath);

  const [currentStep, setCurrentStep] = useState<ApplyWizardStepObject>(
    foundStepBasedOnPath
      ? foundStepBasedOnPath
      : currentPath === '/app/verify/login'
      ? { ...ApplyWizardStepsWithComponents[STEP_VERIFY_LOGIN] }
      : undefined
  );
  // console.log('currentStep', currentStep);
  // console.log('steps', steps);
  // console.log('finalWizardRoutes', finalWizardRoutes);
  const currentStepIndex = currentStep
    ? steps.findIndex((step) => step.id === currentStep.id)
    : steps.findIndex((step) => step.path === currentPath);
  const isInWizard = !!foundStepBasedOnPath;

  // console.log(
  //   'applicationTypeDetermined',
  //   foundStepBasedOnPath,
  //   applicationTypeDetermined,
  //   currentStep,
  //   currentPath,
  //   currentStepIndex
  // );

  useEffect(() => {
    if (applicationTypeDetermined === 'bcm') {
      const newSteps: ApplyWizardStepObject[] = bcmWizardRoutes
        .filter((r: ApplyWizardStep) => !!ApplyWizardStepsWithComponents[r])
        .map((r: ApplyWizardStep) => ApplyWizardStepsWithComponents[r]);
      console.log('[bcm] newSteps', newSteps);
      setSteps([
        ...newSteps,
        ...(newSteps.some((r) => r.id === STEP_VERIFY_LOGIN)
          ? []
          : [ApplyWizardStepsWithComponents[STEP_VERIFY_LOGIN]]),
        ...(disableAuth0ExternalUniversalLogin ? [] : [ApplyWizardStepsWithComponents[STEP_INTEGRATED_LOGIN]]),
      ]);
    }
    if (applicationTypeDetermined === 'bcm-short') {
      const newSteps: ApplyWizardStepObject[] = bcmShortWizardRoutes
        .filter((r: ApplyWizardStep) => !!ApplyWizardStepsWithComponents[r])
        .map((r: ApplyWizardStep) => ApplyWizardStepsWithComponents[r]);
      console.log('[bcm-short] newSteps', newSteps);

      setSteps([
        ...newSteps,
        ...(newSteps.some((r) => r.id === STEP_VERIFY_LOGIN)
          ? []
          : [ApplyWizardStepsWithComponents[STEP_VERIFY_LOGIN]]),
        ...(disableAuth0ExternalUniversalLogin ? [] : [ApplyWizardStepsWithComponents[STEP_INTEGRATED_LOGIN]]),
      ]);
    }
    if (applicationTypeDetermined === 'apply') {
      const newSteps: ApplyWizardStepObject[] = finalWizardRoutes
        // STEP_WHEN_FUNDING_NEEDED removed from the loan flow per Apply QA.
        // See sibling filter in the initial useState above for context.
        .filter((r: ApplyWizardStep) => r !== STEP_WHEN_FUNDING_NEEDED)
        .filter(
          (r: ApplyWizardStep) =>
            !!ApplyWizardStepsWithComponents[r] || !!ApplyWizardStepsWithComponents[`/${r}` as ApplyWizardStep]
        )
        .map(
          (r: ApplyWizardStep) =>
            ApplyWizardStepsWithComponents[r] || ApplyWizardStepsWithComponents[`/${r}` as ApplyWizardStep]
        );
      console.log('[apply] newSteps', newSteps, 'frooom:', finalWizardRoutes);
      setSteps([
        ...newSteps,
        ...(newSteps.some((r) => r.id === STEP_VERIFY_LOGIN)
          ? []
          : [ApplyWizardStepsWithComponents[STEP_VERIFY_LOGIN]]),
        ...(disableAuth0ExternalUniversalLogin ? [] : [ApplyWizardStepsWithComponents[STEP_INTEGRATED_LOGIN]]),
      ]);
    }
    // currentStep sync moved to its own effect below — keying step
    // resolution off `currentPath` (not `applicationTypeDetermined`)
    // means navigations within the same flow (e.g. /app/info →
    // /app/create) actually update currentStep instead of leaving it
    // stuck on the previous wizard step.
  }, [applicationTypeDetermined]);

  // Keep currentStep in sync with currentPath. Without this, navigating
  // from a wizard-step path (e.g. /app/info) to a non-wizard own-shell
  // path (e.g. /app/create, which renders <CreateApplication /> via
  // Application's <Switch>) leaves currentStep pointing at the previous
  // step. Apply/index.tsx then renders the stale step's Component
  // instead of falling through to <Application />, and the user is
  // stuck staring at /app/info content while the URL says /app/create.
  useEffect(() => {
    setCurrentStep(foundStepBasedOnPath ?? undefined);
    // foundStepBasedOnPath is a fresh derived value each render — depend
    // on currentPath so the effect fires only when the path actually
    // changes (and on applicationTypeDetermined since the same path can
    // resolve to different steps under different flow contexts).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPath, applicationTypeDetermined]);

  const [stepDirection, setStepDirection] = useState<'next' | 'prev'>('next');
  const showStepNavigation = steps.some((step) => step.path === currentPath && step.isShowingInNavigation);
  const [saving, setSaving] = useState(false);
  const { logout } = useAuth0();
  const dispatch = useDispatch();

  const changeStepTo = (step: ApplyWizardStep) => {
    const newStep = steps.find((s) => s.id === step);
    console.log('changeStepTo step', step, newStep);
    if (newStep) {
      const newStepIndex = steps.findIndex((s) => s.id === step);
      const farthestStepIndex = steps.findIndex((s) => s.id === farthestStep.id);
      if (farthestStepIndex < newStepIndex || farthestStepIndex < 0) {
        dispatch(setFarthestStepAction(newStep));
      }
      setCurrentStep(newStep);
      console.log('CALLBACK newStep', newStep);
      setCurrentPath(newStep.path);
    }
  };

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    if (typeof window !== 'undefined' && !queryParams.get('code')) {
      window.history.pushState(null, '', currentPath);
    }
  }, [currentPath]);

  // GA4 funnel step views (utils/gaFlow.ts). Wizard steps move with a raw
  // pushState above — the router never sees them — so views are reported
  // from `currentPath`; decision / outcome pages move through react-router,
  // so the history listener covers those. Consecutive duplicates collapse.
  useEffect(() => {
    flowStepForPath(currentPath);
  }, [currentPath]);
  useEffect(() => {
    flowStepForPath(window.location.pathname);
    const unlisten = history.listen((loc: { pathname: string }) => flowStepForPath(loc.pathname));
    return unlisten;
  }, []);

  useEffect(() => {
    if (location.pathname) {
      setCurrentPath(location.pathname);
    }
  }, [location]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const handlePopState = () => {
        setCurrentPath(location.pathname);
      };

      window.addEventListener('popstate', handlePopState);

      return () => {
        window.removeEventListener('popstate', handlePopState);
      };
    }
  }, []);

  const changeStepToFirst = () => {
    changeStepTo(
      steps[0].id !== STEP_HOMEPAGE &&
        steps[0].id !== STEP_BCM_HOMEPAGE &&
        steps[0].id !== STEP_BCM_2_HOMEPAGE &&
        steps[0].id !== STEP_EQ_LEAD_HOMEPAGE
        ? steps[0].id
        : steps[1].id
    );
  };

  const changeStepToFirstNotValid = () => {
    const latestValues = getFormValues('application')(store.getState() as RootState) || {};
    let firstNotValidStep = null;
    for (let i = 0; i < steps.length; i++) {
      try {
        steps[i].zodSchema.parse(latestValues);
      } catch (error) {
        firstNotValidStep = steps[i];
        break;
      }
    }

    changeStepTo(firstNotValidStep ? firstNotValidStep.id : STEP_SUMMARY);
  };

  const changeStepAfterLogin = () => {
    // GA4 funnel: the sign-in / account hop completed.
    flowStepCompleteForPath(window.location.pathname);
    if (applicationTypeDetermined === 'bcm') {
      const infoIndex = steps.findIndex((step) => step.id === ApplyWizardStepsWithComponents[STEP_BCM_INFO].id);
      if (infoIndex === -1) {
        changeStepTo(steps[0].id);
      } else {
        if (infoIndex + 1 >= steps.length) {
          changeStepTo(steps[steps.length - 1].id);
        } else {
          changeStepTo(steps[infoIndex + 1].id);
        }
      }
    } else if (applicationTypeDetermined === 'bcm-short') {
      const infoIndex = steps.findIndex((step) => step.id === ApplyWizardStepsWithComponents[STEP_BCM_2_INFO].id);
      if (infoIndex === -1) {
        changeStepTo(steps[0].id);
      } else {
        if (infoIndex + 1 >= steps.length) {
          changeStepTo(steps[steps.length - 1].id);
        } else {
          changeStepTo(steps[infoIndex + 1].id);
        }
      }
    } else {
      const infoIndex = steps.findIndex((step) => step.id === ApplyWizardStepsWithComponents[STEP_INFO].id);
      if (infoIndex === -1) {
        changeStepTo(steps[0].id);
      } else {
        if (infoIndex + 1 >= steps.length) {
          changeStepTo(steps[steps.length - 1].id);
        } else {
          changeStepTo(steps[infoIndex + 1].id);
        }
      }
    }
  };

  const resetFarthestStep = () => {
    const stepsWithCompletion = steps.map((step) => ({
      ...step,
      isComplete: step.isComplete(allValues),
    }));
    let latestCompletedStep = null;
    for (let i = 0; i < stepsWithCompletion.length; i++) {
      if (stepsWithCompletion[i].isComplete) {
        latestCompletedStep = steps[i];
      } else {
        break;
      }
    }

    if (latestCompletedStep) {
      dispatch(setFarthestStepAction(latestCompletedStep));
    } else {
      dispatch(
        setFarthestStepAction(
          applicationTypeDetermined === 'bcm'
            ? { ...ApplyWizardStepsWithComponents[STEP_BCM_HOMEPAGE] }
            : applicationTypeDetermined === 'bcm-short'
            ? { ...ApplyWizardStepsWithComponents[STEP_BCM_2_HOMEPAGE] }
            : { ...ApplyWizardStepsWithComponents[STEP_HOMEPAGE] }
        )
      );
    }
  };

  useEffect(() => {
    if (foundStepBasedOnPath) {
      setCurrentStep(
        foundStepBasedOnPath
          ? foundStepBasedOnPath
          : currentPath === '/app/verify/login'
          ? { ...ApplyWizardStepsWithComponents[STEP_VERIFY_LOGIN] }
          : undefined
      );
    }
  }, [foundStepBasedOnPath]);

  useEffect(() => {
    checkUser(user, match, dispatch, history, logout);
  }, []);

  const dataLayerPush = (event: any, post: any) => {
    // shallow copy the post object
    let data = { ...post };

    // clear out any blacklisted/sensitive fields
    Object.keys(data).forEach(function (key) {
      if (/code|dob|ssn|bank/.test(key)) {
        delete data[key];
      }
    });

    const dataLayerObj = Object.assign({ event: event }, data);

    TagManager.dataLayer({
      dataLayer: {
        ...dataLayerObj,
      },
    });
  };

  const nextRoute = () => {
    console.log('currentStep', currentStep);
    // GA4 funnel: every wizard step's "continue" comes through here.
    flowStepCompleteForPath(currentStep.path);
    if (applicationTypeDetermined === 'bcm' && currentStep.id === STEP_BCM_SUMMARY) {
      history.push('/bcm2/app/contact-soon');
    } else {
      const currentStepIndex = steps.findIndex((r) => r.id === currentStep.id);
      console.log('currentStepIndex', currentStepIndex);
      console.log('next step is:', steps[currentStepIndex + 1]);
      setStepDirection('next');
      if (currentStep.id === STEP_SUMMARY) {
        history.push('/app/decision/process');
      } else {
        changeStepTo(steps[currentStepIndex + 1].id);
      }
    }
  };

  const previousRoute = () => {
    const currentStepIndex = steps.findIndex((r) => r.id === currentStep.id);
    setStepDirection('prev');
    if (currentStepIndex === 0) {
    } else {
      changeStepTo(steps[currentStepIndex - 1].id);
    }
  };

  return (
    <ApplyWizardContext.Provider
      value={{
        applicationFlow: applicationTypeDetermined,
        nextRoute,
        previousRoute,
        dataLayerPush,
        currentStepIndex,
        saving,
        setSaving,
        steps,
        currentStep,
        changeStepTo,
        changeStepToFirstNotValid,
        resetFarthestStep,
        changeStepToFirst,
        farthestStep,
        isInWizard,
        showStepNavigation,
        currentPath,
        setCurrentPath,
        changeStepAfterLogin,
        setStepDirection,
        stepDirection,
        temp,
      }}
    >
      {children}
    </ApplyWizardContext.Provider>
  );
};

const selector = formValueSelector('application');

const mapStateToProps = (state: RootState) => {
  // attempt to ensure business_id is set
  const bizId = selector(state, 'business_id');
  const acctId = bizId ? bizId : state.user.accountId;

  return {
    farthestStep: state.wizardContext.farthestStep,
    routes: state.brand?.data?.routes,
    finalWizardRoutes: state.wizardRouting.finalWizardRoutes,
    vendor_name: state.brand?.data?.vendor_name,
    business_id: acctId,
    applicationType: selector(state, 'application_type'),
    loan_id: selector(state, 'loan_id'),
    contact_id: selector(state, 'owner_1_id'),
    loan_type: selector(state, 'loan_type'),
    loan_terms: selector(state, 'loan_terms'),
    allValues: getFormValues('application')(state) || {},
    theme: state.theme,
    config: state.config,
    temp: state.temp,
    user: state.user,
    images: state.brand?.data?.media,
    partnerCustomFields: state.partnerCustomFields,
  };
};

export const ApplyWizardProvider = withRouter(
  connect(mapStateToProps, { setUser, change })(ApplyWizardProviderConnected)
);

export const useApplyWizardContext = () => {
  const context = useContext(ApplyWizardContext);
  return context;
};
